import { Link, useNavigate } from 'react-router-dom'
import { ShoppingCart, Bell, Sun, Moon } from 'lucide-react'
import { useCartStore, useAppStore, useThemeStore } from '../../store/index.js'
import { useQuery } from '@tanstack/react-query'
import { promotionService } from '../../services/api.js'
import { useAuthStore } from '../../store/index.js'

export default function TopHeader({ title, showBack, backTo, showCart = true, showLogo = false }) {
  const count     = useCartStore(s => s.count)
  const isOnline  = useAppStore(s => s.isOnline)
  const navigate  = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { isDark, toggle: toggleTheme } = useThemeStore()

  const { data: promoData } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => promotionService.getAll(),
    enabled: isAuthenticated,
    staleTime: 3 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
  })
  const promoList   = Array.isArray(promoData) ? promoData : (promoData?.promotions ?? promoData?.items ?? [])
  const unreadCount = promoList.filter(p => !p.read_at && !p.dismissed_at).length

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-surface-dark/95 backdrop-blur-md border-b border-surface-border"
      style={{ paddingTop: 'var(--safe-top)' }}>
      <div className="flex items-center px-4 py-3 h-16 relative">

        {/* Left side */}
        <div className="flex items-center gap-3 z-10">
          {showBack ? (
            <button
              onClick={() => backTo ? navigate(backTo) : navigate(-1)}
              className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center active:bg-surface-border transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
            </button>
          ) : showLogo ? (
            <Link to="/" className="flex items-center">
              <img src="/logo.text-w.png" alt="Tony's Liquor" className="h-8 w-auto object-contain" />
            </Link>
          ) : null}

          {title ? (
            <span className="font-heading text-white text-lg">{title}</span>
          ) : !showLogo ? (
            <img src="/logo.text-w.png" alt="Tony's Liquor" className="h-7 w-auto object-contain" />
          ) : null}
        </div>

        {/* Centre logo — always visible when showBack is true */}
        {showBack && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <img src="/logo.text-w.png" alt="Tony's Liquor" className="h-6 w-auto object-contain opacity-80" />
          </div>
        )}

        {/* Right side — push to far right */}
        <div className="ml-auto z-10">
        <div className="flex items-center gap-2">
          {!isOnline && (
            <span className="text-[10px] font-display text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full">Offline</span>
          )}

          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center active:bg-surface-border transition-colors"
            aria-label="Toggle theme"
          >
            {isDark
              ? <Sun size={17} className="text-amber-400" />
              : <Moon size={17} className="text-primary" />}
          </button>

          <Link to="/notifications" className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center relative active:bg-surface-border transition-colors">
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-white text-[9px] font-bold font-display">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Link>
          {showCart && (
            <Link to="/cart" className="w-9 h-9 rounded-xl bg-surface-elevated flex items-center justify-center relative active:bg-surface-border transition-colors">
              <ShoppingCart size={18} />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center text-white text-[10px] font-bold font-display">
                  {count > 9 ? '9+' : count}
                </span>
              )}
            </Link>
          )}
          </div>
        </div>
      </div>
    </header>
  )
}
